import SuggestedUsersTextField from "@/components/communications-chat-components/SuggestedUsersTextField";
import { UserAvatarSx } from "@/components/communications-chat-components/utilStyles";
import UserAvatar from "@/components/lazo-ai-chat-components/Avatar";
import CommonButton from "@/components/utils/CommonButton";
import { CopyIcon } from "@/components/utils/icons/CopyIcon";
import { DownloadIcon } from "@/components/utils/icons/DownloadIcon";
import { LeaveIcon } from "@/components/utils/icons/LeaveIcon";
import { Colors } from "@/enums/Colors";
import User from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/core/User";
import NotificationService from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/services/NotificationService";
import { LazoFile } from "@/models/LazoFile";
import { LazoChatConversationShell, mapToShell } from "@/models/ai-chat/LazoChatConversation";
import { SmallConversationMember } from "@/models/ai-chat/SmallConversationMember";
import LazoFileService from "@/services/LazoFileService";
import LazoChatConversationService from "@/services/ai-chat/LazoChatConversationService";
import { ReplayOutlined } from "@mui/icons-material";
import { TabContext, TabPanel } from "@mui/lab";
import {
  Box,
  Divider,
  List,
  ListItem,
  SxProps,
  Tab,
  Tabs,
  Typography,
  buttonClasses,
  listItemClasses,
  styled,
  svgIconClasses,
  tabClasses,
  tabsClasses,
} from "@mui/material";
import { HttpStatusCode } from "axios";
import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { MentionItem } from "react-mentions";
import fileAvatar from "../../../../assets/images/FileAvatar.svg";
import { usePortalChatContext } from "./PortalChatContext";

type UserWithRemove = User & {
  remove: boolean;
};

export const PortalChatSettingsComponent = ({
  conversation,
  currentUserExternalId,
  onChatSelect,
  handleClose,
  setChats,
}: {
  conversation: LazoChatConversationShell;
  currentUserExternalId: string;
  handleClose: () => void;
  onChatSelect: (chat: LazoChatConversationShell) => void;
  setChats: Dispatch<SetStateAction<LazoChatConversationShell[]>>;
}) => {
  const { t } = useTranslation("chat");
  const { members: currentMembers, setMembers: setCurrentMembers, setMessages } = usePortalChatContext();

  const [value, setValue] = React.useState(0);
  const [conversationFiles, setConversationFiles] = useState<LazoFile[]>([]);

  const [members, setMembers] = useState<UserWithRemove[]>([]);
  const [membersToAdd, setMembersToAdd] = useState<string[]>([]);

  const [fileAnchor, setFileAnchor] = useState<HTMLLIElement | null>(null);
  const isOwner: boolean = currentMembers.find((m) => m.userExternalId === currentUserExternalId)?.isOwner || false;

  const handleChange = (_: any, newValue: number) => {
    setValue(newValue);
  };

  const tabItemStyle = (isActive: boolean) => ({
    textTransform: "none",
    fontWeight: isActive ? "bold" : "normal",
    fontSize: "1rem",
    borderBottom: isActive ? "1px solid" : "none",
    borderColor: isActive ? "currentColor" : "",
    boxSizing: "border-box",
  });

  const formatDate = (date: Date) => {
    const options: Intl.DateTimeFormatOptions = {
      year: "numeric",
      month: "short",
      day: "2-digit",
    };
    return new Date(date).toLocaleDateString("en-US", options);
  };

  useEffect(() => {
    if (!conversation) return;
    LazoChatConversationService.getConversationFiles(conversation?.externalId).then((response) => {
      if (response.onSuccess) {
        setConversationFiles(response.getContentOrThrowError());
      } else {
        console.log(response.onError);
      }
    });
  }, [conversation]);

  useEffect(() => {
    setMembers(
      currentMembers.reduce((arr, m) => {
        if (m.userExternalId !== currentUserExternalId) {
          arr.push({
            firstName: m.firstName,
            lastName: m.lastName,
            externalId: m.userExternalId,
            remove: false,
            email: m.email,
          });
        }
        return arr;
      }, [])
    );
  }, [currentMembers.length]);

  function handleRemoveClick(idx: number) {
    setMembers((prev) =>
      prev.map((m, i) => {
        if (i === idx) {
          return { ...m, remove: true };
        }
        return m;
      })
    );
  }

  function handleUndo(idx: number) {
    setMembers((prev) =>
      prev.map((m, i) => {
        if (i === idx) {
          return { ...m, remove: false };
        }
        return m;
      })
    );
  }

  function handleCancel() {
    handleClose();
  }

  function handleConfirmClick() {
    const usersToRemove = members.filter((m) => m.remove).map((user) => user.externalId);
    if (usersToRemove.length > 0 || membersToAdd.length > 0) {
      LazoChatConversationService.manageUsers(
        membersToAdd.map((externalId) => externalId),
        usersToRemove,
        conversation.externalId
      ).then((r) => {
        {
          r.onSuccess((c) => {
            setMembersToAdd([]);
            setCurrentMembers(
              c.members.map(
                ({ user, isOwner }) =>
                  ({
                    firstName: user.firstName,
                    lastName: user.lastName,
                    userExternalId: user.externalId,
                    isOwner: isOwner,
                  }) as SmallConversationMember
              )
            );
            setMessages(c.messages);

            const mappedConv = mapToShell(c);
            setChats((prev) =>
              prev.map((p) => {
                if (p.externalId === c.externalId) {
                  return mappedConv;
                }
                return p;
              })
            );
            onChatSelect(mappedConv);

            NotificationService.info(t("settings.notifications.operationSuccessful"));

            handleClose();
          });
          r.onError((e) => {
            if (r.getHttpStatusCode() === HttpStatusCode.BadRequest) {
              NotificationService.warn(e);
            } else {
              NotificationService.error(t("settings.notifications.errorUpdatingMembers") + e);
            }
          });
        }
      });
    }
  }

  function handleMentionsChange(_: any, mentions: MentionItem[]) {
    setMembersToAdd(
      mentions.reduce((filtered, item) => {
        if (!filtered.includes(item.id)) {
          filtered.push(item.id);
        }
        return filtered;
      }, [])
    );
  }

  function handleFileMouseEnter(ev: React.MouseEvent<HTMLLIElement>) {
    if (ev.currentTarget !== fileAnchor) {
      setFileAnchor(ev.currentTarget);
    }
  }

  function handleFileMouseLeave(_: React.MouseEvent<HTMLLIElement>) {
    if (fileAnchor !== null) {
      setFileAnchor(null);
    }
  }

  function handleDownloadFile(externalId: string, fileName: string) {
    LazoFileService.downloadFile(externalId, fileName);
  }

  function handleCopyMemberNames() {
    let names = "";
    currentMembers.forEach((m, idx) => {
      names += m.firstName + " " + m.lastName;
      if (idx !== currentMembers.length - 1) {
        names += ", ";
      }
    });
    navigator.clipboard
      .writeText(names)
      .then(() => NotificationService.info(t("settings.notifications.namesCopied")))
      .catch(() => {});
  }

  function handleLeaveChat() {
    if (currentMembers.length <= 2) {
      NotificationService.warn(t("settings.notifications.cantLeaveConversation"));
      return;
    }
    LazoChatConversationService.leaveConversation(conversation.externalId).then((r) => {
      r.onSuccess(() => {
        setChats((prev) => prev.filter((c) => c.externalId !== conversation.externalId));
        onChatSelect(null);
        handleClose();
      });
      r.onError((e) => {
        NotificationService.error(t("settings.notifications.errorLeavingChat") + e);
      });
    });
  }

  return (
    <MainContainer>
      <Box>
        <TabContext value={value.toString()}>
          <Box sx={{ padding: "0 1.5rem", maxWidth: "100%" }}>
            <StyledTabs onChange={handleChange} variant="fullWidth" TabIndicatorProps={{ style: { display: "none" } }} value={value}>
              <Tab label={t("settings.tabs.members")} sx={tabItemStyle(value === 0)} value={0} />
              <Tab label={t("settings.tabs.files")} value={1} sx={tabItemStyle(value === 1)} />
              <Tab label={t("settings.tabs.settings")} value={2} sx={tabItemStyle(value === 2)} />
            </StyledTabs>
          </Box>
          <Box>
            <StyledTabPanel sx={{ padding: "1.5rem 1.5rem 0 1.5rem" }} value={"0"}>
              <TabContent>
                <SectionTypography>{t("settings.members.title")}</SectionTypography>
                <StyledList>
                  {members.map((m, idx) => (
                    <ListItem key={m.externalId}>
                      <AvatarContainer>
                        <UserAvatar
                          user={
                            {
                              firstName: m.firstName,
                              lastName: m.lastName,
                            } as User
                          }
                          sx={CustomUserAvatarSx}
                        />
                        <Box display="flex" flexDirection="column" justifyContent="center">
                          <Typography fontSize={"0.75rem"} fontWeight={500}>
                            {m.firstName + " " + m.lastName}
                          </Typography>
                          <Typography fontSize={"0.75rem"} fontWeight={500} sx={{ color: Colors.DARKER_DARKER_GRAY }}>
                            {m.email}
                          </Typography>
                        </Box>
                      </AvatarContainer>
                      {conversation.membersCount > 2 &&
                        (!m.remove ? (
                          <CommonButton onClick={() => handleRemoveClick(idx)} purpleBorderVariant style={{ height: "1.688rem" }}>
                            {t("settings.members.remove")}
                          </CommonButton>
                        ) : (
                          <CommonButton onClick={() => handleUndo(idx)} purpleBorderVariant style={{ height: "1.688rem" }}>
                            <ReplayOutlined sx={{ transform: "rotate(-60deg)" }} />
                            <Typography>{t("settings.members.undo")}</Typography>
                          </CommonButton>
                        ))}
                    </ListItem>
                  ))}
                </StyledList>

                {!conversation.readOnly && (
                  <>
                    <Divider />
                    <AddPeopleContainer>
                      <Typography fontWeight={500} fontSize={"0.75rem"}>
                        {t("settings.members.addPeople")}
                      </Typography>
                      <SuggestedUsersTextField
                        containerSx={{ padding: 0, border: 0 }}
                        hideTo={true}
                        placeholder={t("settings.members.typeName")}
                        onMentionsChange={handleMentionsChange}
                        conversationExternalId={conversation.externalId}
                        mentionStyle={{ ...MentionInputStyle }}
                      />
                    </AddPeopleContainer>
                    <ButtonsContainer>
                      <CommonButton purpleBorderVariant onClick={handleCancel} style={{ borderRadius: "0.5rem" }}>
                        {t("settings.members.cancel")}
                      </CommonButton>
                      <CommonButton onClick={handleConfirmClick}>{t("settings.members.confirm")}</CommonButton>
                    </ButtonsContainer>
                  </>
                )}
              </TabContent>
            </StyledTabPanel>
            <StyledTabPanel value={"1"}>
              <TabContent>
                <Box
                  sx={{
                    display: "flex",
                    gap: "0.5rem",
                    flexDirection: "column",
                  }}
                >
                  <SectionTypography padding={"0 1.5rem"}>{t("settings.files.title")}</SectionTypography>
                  <FileList>
                    {conversationFiles.map((file) => (
                      <ListItem onMouseEnter={handleFileMouseEnter} onMouseLeave={handleFileMouseLeave} key={file.externalId}>
                        <img alt={"file-img"} src={fileAvatar.src} />
                        <FilenameDetailsContainer>
                          <FilenameTypography variant="h6">{file.name}</FilenameTypography>
                          <FiledetailsTypography variant="h6">
                            {t("settings.files.sharedBy")} {file.submitterUser.firstName} {file.submitterUser.lastName}{" "}
                            {t("settings.files.on")} {formatDate(new Date(file.createdAt))}
                          </FiledetailsTypography>
                        </FilenameDetailsContainer>
                        {fileAnchor && (
                          <FileMenuContainer>
                            <DownloadIcon onClick={() => handleDownloadFile(file.externalId, file.name)} />
                          </FileMenuContainer>
                        )}
                      </ListItem>
                    ))}
                    {conversationFiles.length === 0 && (
                      <ListItem>
                        <FilenameTypography variant="h6">{t("settings.files.noFiles")}</FilenameTypography>
                      </ListItem>
                    )}
                  </FileList>
                </Box>
              </TabContent>
            </StyledTabPanel>
            <StyledTabPanel value={"2"}>
              <TabContent padding={"0 1.5rem"}>
                <ButtonGroupContainer>
                  <StyledButton onClick={handleCopyMemberNames} borderBottom={`1px solid ${Colors.LIGHT_GRAY_DEDEE1}`}>
                    <CopyIcon /> {t("settings.general.copyMemberNames")}
                  </StyledButton>
                  {!conversation.readOnly && (
                    <StyledButton onClick={handleLeaveChat}>
                      <LeaveIcon /> {t("settings.general.leaveChat")}
                    </StyledButton>
                  )}
                </ButtonGroupContainer>
              </TabContent>
            </StyledTabPanel>
          </Box>
        </TabContext>
      </Box>
    </MainContainer>
  );
};

const StyledTabs = styled(Tabs)(() => ({
  [`&.${tabsClasses.root}`]: {
    color: Colors.BLACK_151517,
    minHeight: 0,
    borderBottom: `1px solid ${Colors.LIGHT_GRAY_DEDEE1}`,
    height: "auto",
    [`& .${tabClasses.root}`]: {
      color: Colors.LIGHT_GRAY_8A8E93,
      fontSize: "0.75rem",
      fontWeight: 400,
      margin: 0,
      padding: "0.5rem 0.75rem",
      minHeight: "1.25rem",
      height: "auto",
      [`&.${tabClasses.selected}`]: {
        color: Colors.BLACK_151517,
      },
    },
  },
}));

const TabContent = styled(Box)(() => ({
  display: "flex",
  gap: "1rem",
  flexDirection: "column",
  maxWidth: "24.125rem",
}));

const StyledList = styled(List)(() => ({
  padding: 0,
  display: "flex",
  flexDirection: "column",
  gap: "0.5rem",
  maxHeight: "17.625rem",
  overflow: "hidden",
  overflowY: "auto",
  [`& .${listItemClasses.root}`]: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: 0,
    margin: 0,
    height: "auto",
    gap: "0.5rem",
  },
}));

const SectionTypography = styled(Typography)(() => ({
  fontSize: "0.75rem",
  fontWeight: "500",
}));

const StyledTabPanel = styled(TabPanel)(() => ({
  padding: 0,
  paddingTop: "1.5rem",
}));

const AvatarContainer = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  gap: "0.5rem",
}));

const CustomUserAvatarSx: SxProps = {
  ...UserAvatarSx,
  width: "24px",
  height: "24px",
  minWidth: "24px",
  minHeight: "24px",
  fontSize: "0.75rem",
  borderRadius: "0.25rem",
};

const ButtonsContainer = styled(Box)(() => ({
  display: "flex",
  paddingTop: "0.5rem",
  alignItems: "center",
  gap: "1rem",
  width: "100%",
  [`& .${buttonClasses.root}`]: {
    flex: 1,
    minHeight: 0,
    padding: "0.75rem 1rem",
    height: "auto",
    borderRadius: "0.5rem",
    fontSize: "0.875rem",
    fontWeight: 600,
  },
}));

const MainContainer = styled(Box)(() => ({
  display: "flex",
  gap: "1.5rem",
  flexDirection: "column",
  maxWidth: "24.125rem",
}));

const AddPeopleContainer = styled(Box)(() => ({
  display: "flex",
  flexDirection: "column",
  gap: "1rem",
}));

const MentionInputStyle = {
  "&singleLine": {
    control: {
      flex: 1,
    },
    highlighter: {
      position: "absolute",
      border: "none",
      outline: "none",
    },
    input: {
      position: "relative",
      flex: 1,
      fontWeight: 500,
      border: `1px solid ${Colors.STROKE_DARK_GRAY}`,
      padding: "0.75rem",
      borderRadius: "0.5rem",
      outline: "none",
    },
  },
};

const FilenameDetailsContainer = styled(Box)(() => ({
  display: "flex",
  alignItems: "start",
  justifyContent: "center",
  flexDirection: "column",
  width: "100%",
}));

const FileList = styled(List)(() => ({
  padding: 0,
  display: "flex",
  flexDirection: "column",
  maxHeight: "17.625rem",
  overflow: "hidden",
  overflowY: "auto",
  [`& .${listItemClasses.root}`]: {
    display: "flex",
    alignItems: "center",
    padding: "0.5rem 1.5rem",
    margin: 0,
    height: "auto",
    gap: "0.5rem",
    ":hover": {
      backgroundColor: Colors.LIGHT_GRAY_BG_F6F8FA,
      transition: "background-color 0.1s ease-in",
    },
  },
}));

const FilenameTypography = styled(Typography)(() => ({
  display: "block",
  width: "85%",
  fontSize: "12px",
  fontWeight: "500",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));

const FiledetailsTypography = styled(Typography)(() => ({
  display: "block",
  fontSize: "12px",
  fontWeight: "400",
  color: Colors.LIGHT_GRAY_8A8E93,
  width: "85%",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  ":hover": {
    whiteSpace: "unset",
  },
}));

const FileMenuContainer = styled(Box)(() => ({
  display: "flex",
  alignItems: "start",
  gap: "0.5rem",
  borderRadius: "0.125rem",
  border: `1px solid ${Colors.LIGHT_GRAY_DEDEE1}`,
  padding: "0.25rem",
  backgroundColor: "#fff",
  [`& .${svgIconClasses.root}`]: {
    ":hover": {
      cursor: "pointer",
    },
  },
}));

const ButtonGroupContainer = styled(Box)(() => ({
  display: "flex",
  flexDirection: "column",
  borderRadius: "0.5rem",
  border: `1px solid ${Colors.LIGHT_GRAY_DEDEE1}`,
}));

const StyledButton = styled(Box)(() => ({
  display: "flex",
  gap: "0.5rem",
  padding: "0.75rem 1rem",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "0.875rem",
  fontWeight: 600,
  ":hover": {
    cursor: "pointer",
    transition: "background-color .1s ease-in",
    backgroundColor: Colors.LIGHT_GRAY_BG_F6F8FA,
  },
}));

export default PortalChatSettingsComponent;
